import { cloneDeep } from "lodash";
import {
    allClassificationTypesOptions,
    allDataSourcesOptions,
    allMeasureUnitsOptions,
    allMutuallyRedeemingLevelOptions,
    allMutuallyRedeemingOptions,
    allPeriodicitiesOptions,
    allReportNamesOptions,
    allReportTypesOptions,
} from "../../common/baseOptions";
import { Classificator } from "../../common/types/Classificator";
import { Draggable } from "../../common/types/Draggable";
import { Option } from "../../common/types/Option";
import { Template } from "../../common/types/Template";
import { ByForm, Segmentation } from "../../common/types/roots/fields";
import { Kf219CoFilterOptions } from "./options/Kf219CoFilterOptions";
import { Kf219CoFilterSettings } from "./settings/Kf219CoFilterSettings";

/** Блок "Доходы": по умолчанию все уровни включены, отбор кодов не задан (§3 ТЗ) */
export const kf219CoIncomeClassificators: Classificator[] = [
    { key: 'kat', active: true, selected: [] },
    { key: 'cls', active: true, selected: [] },
    { key: 'pcl', active: true, selected: [] },
    { key: 'spf', active: true, selected: [] },
]

export const kf219CoByFormOptions: Option<ByForm>[] = [
    { value: '2-19', label: '2-19', labelKk: '2-19', labelEn: '2-19' },
]

/** Разрезность: только КБК и Регион (без ГУ), по умолчанию оба включены в порядке КБК, Регион */
export const kf219CoSegmentationOptions: Draggable<Segmentation>[] = [
    { value: 'KBK', label: "КБК", labelKk: "БСК", labelEn: "KBK", active: true },
    { value: 'REGION', label: "Регион", labelKk: "Аймақ", labelEn: "Region", active: true },
]

// Нужна как временная затычка (при создании нового берутся пустые настройки)
const tempSettings: Kf219CoFilterSettings = {
    // Base:
    reportName: 'KF219-CO',
    reportType: 'REGULATED',
    classificationType: 'INCOMES',
    dataSource: 'IISK',
    periodicity: 'PERIOD',
    // Custom:
    period: { from: '', to: '' },
    region: null,
    byForm: '2-19',
    measureUnit: 'THOUSAND',
    mutuallyRedeeming: 'WITH',
    mutuallyRedeemingLevels: [],
    segmentations: [],
    incomes: [],
    roundUpTo: 1, // «Округлять результат до» включён, 1 знак — как в шаблоне выгрузки
    msuAggregation: true,
    isTree: true,
    fillCbc: false,
    indicators: [],
}

export const kf219CoBaseTemplate: Template = {
    id: null,
    name: 'Отчет по КФ 2-19 — Контингент и отчисления',
    excelName: null,
    description: null,
    category: '100',
    regions: [],
    userId: '',
    filterSettings: tempSettings,
}

export const kf219CoFilterOptions: Kf219CoFilterOptions = {
    // Base:
    key: 'KF219-CO',
    reportNames: cloneDeep(allReportNamesOptions),
    reportTypes: cloneDeep(allReportTypesOptions),
    classificationTypes: cloneDeep(allClassificationTypesOptions.filter(it => it.value === 'INCOMES')),
    dataSources: cloneDeep(allDataSourcesOptions.filter(it => it.value === 'IISK' || it.value === 'LOADER')),
    periodicities: cloneDeep(allPeriodicitiesOptions),
    // Custom:
    regions: [], // Загружается с бэка
    byForms: cloneDeep(kf219CoByFormOptions),
    measureUnits: cloneDeep(allMeasureUnitsOptions),
    mutuallyRedeeming: cloneDeep(allMutuallyRedeemingOptions),
    mutuallyRedeemingLevels: cloneDeep(allMutuallyRedeemingLevelOptions),
    segmentations: cloneDeep(kf219CoSegmentationOptions),
    incomes: cloneDeep(kf219CoIncomeClassificators),
}

/**
 * Доступность переключателя "Агрегация по МСУ" (§3 ТЗ): "Регион" в разрезности и выбран XX0000 или XXYY00
 * (YY не 00/01), кроме областей 59, 60, 62. Условия по полю "Бюджет" нет — поля в отчёте нет.
 */
export function isKf219CoMsuAggregationAvailable(regionCode: string | null | undefined, segmentations: Draggable<Segmentation>[] | null | undefined): boolean {
    if (!regionCode || regionCode.length !== 6 || ['59', '60', '62'].includes(regionCode.slice(0, 2))) {
        return false
    }
    const isWholeObl = regionCode.endsWith('0000')
    const isDistrictNode = regionCode.endsWith('00') && !['00', '01'].includes(regionCode.slice(2, 4))
    if (!isWholeObl && !isDistrictNode) {
        return false
    }
    return (segmentations ?? []).some(it => it.value === 'REGION' && it.active)
}
